import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography, Paper, Alert } from '@mui/material';
import { Refresh as RefreshIcon, WifiOff as WifiOffIcon } from '@mui/icons-material';
import { useUserRole } from '../hooks/useUserRole';
import { AdminLayout } from './AdminLayout';
import { UnauthorizedAccess } from './UnauthorizedAccess';
import { UserPortal } from './UserPortal';

/**
 * Protected route component that checks if the user has control panel access
 * Only users with roleId ≤ 2 can access the Control Panel
 */
export const AdminRoute: React.FC = () => {
  const { user, isAdmin, isLoading, error, isNetworkError, refetch } = useUserRole();
  const [adminViewMode, setAdminViewMode] = useState<'admin' | 'portal'>('portal');
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  // Show loading state while checking user role
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f8fafc'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Checking Permissions...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verifying your access to the Control Panel
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Show error state if user info couldn't be loaded
  if (error) {
    if (isNetworkError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3, maxWidth: 400 }}>
            <WifiOffIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Unable to reach the server
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your account details couldn't be loaded because the server is unreachable. Please check your connection and try again.
            </Typography>
            <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              startIcon={isRetrying ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying…' : 'Try Again'}
            </Button>
          </Paper>
        </Box>
      );
    }

    return (
      <UnauthorizedAccess
        user={user}
        reason={`Unable to verify your permissions: ${error}`}
      />
    );
  }

  // Non-admin users get the user portal
  if (!isAdmin) {
    if (!user) {
      return (
        <UnauthorizedAccess
          user={user}
          reason="Your account information could not be loaded. Please try signing in again."
        />
      );
    }
    return <UserPortal user={user} />;
  }

  // Admin users can switch between the control panel and their own user portal
  if (adminViewMode === 'portal' && user) {
    return (
      <UserPortal
        user={user}
        onSwitchToAdmin={() => setAdminViewMode('admin')}
      />
    );
  }

  // Default: admin control panel
  return <AdminLayout user={user} onSwitchToPortal={() => setAdminViewMode('portal')} />;
};
